import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { useLoading } from '../../contexts/LoadingContext';
import { useSocket } from '../../contexts/SocketContext';
import { ShoppingCart, Eye, Search, MousePointerClick, Clock, ArrowRight, ShoppingBag, Package, Calendar } from 'lucide-react';

const CustomerRow = ({ customer, onCustomerClick, isOnline }) => {
  const navigate = useNavigate();
  return (
    <tr className="hover:bg-admin-slate-50 dark:hover:bg-admin-slate-700/50 transition-colors duration-200">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="h-10 w-10 flex-shrink-0">
            <div className="h-10 w-10 rounded-full bg-admin-ucla-100 dark:bg-admin-ucla-900/30 flex items-center justify-center relative">
              <span className="text-admin-ucla-600 dark:text-admin-ucla-400 font-medium text-sm">
                {customer.name.charAt(0)}
              </span>
              {/* Online indicator */}
              {isOnline && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-admin-slate-800 animate-pulse"></div>
              )}
            </div>
          </div>
          <div className="ml-4">
            <button
              onClick={() => onCustomerClick(customer)}
              className="text-sm font-medium text-admin-ucla-600 dark:text-admin-ucla-700 hover:text-admin-ucla-500 dark:hover:text-admin-ucla-900 transition-colors duration-200 text-left flex items-center gap-2"
            >
              {customer.name}
              {isOnline && (
                <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                  Online
                </span>
              )}
            </button>
            <div className="text-sm text-admin-slate-500 dark:text-admin-slate-400">{customer.email}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="text-sm text-admin-slate-900 dark:text-admin-slate-100">
          {customer.phone || 'Not provided'}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="text-sm text-admin-slate-900 dark:text-admin-slate-100">{customer.totalOrders || 0}</span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="text-sm text-admin-slate-900 dark:text-admin-slate-100">Rs. {customer.totalSpent || 0}</span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          customer.status === 'active' 
            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' 
            : customer.status === 'blocked' 
              ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400' 
              : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400'
        }`}>
          {customer.status}
        </span>
        {customer.blockReason && (
          <span className="block mt-1 text-xs text-admin-slate-500 dark:text-admin-slate-400">
            Reason: {customer.blockReason}
          </span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-admin-slate-500 dark:text-admin-slate-400">
        {customer.lastPurchase ? new Date(customer.lastPurchase).toLocaleDateString() : 'No purchases yet'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex items-center space-x-3 justify-end">
          <button 
            onClick={() => navigate(`/admin/customers/${customer._id}/orders`)}
            className="text-admin-ucla-500 hover:text-admin-ucla-600 dark:text-admin-ucla-700 dark:hover:text-admin-ucla-900 transition-colors duration-200"
          >
            View Orders
          </button>
        </div>
      </td>
    </tr>
  );
};

const LoadingSkeleton = () => (
  <div className="space-y-6">
    {/* Header Skeleton */}
    <div className="sm:flex sm:items-center sm:justify-between animate-pulse">
      <div>
        <div className="h-8 w-32 bg-admin-slate-200 dark:bg-admin-slate-700 rounded"></div>
        <div className="mt-1 h-4 w-64 bg-admin-slate-200 dark:bg-admin-slate-700 rounded"></div>
      </div>
      <div className="mt-4 sm:mt-0">
        <div className="h-10 w-32 bg-admin-slate-200 dark:bg-admin-slate-700 rounded-md"></div>
      </div>
    </div>

    {/* Filters Skeleton */}
    <div className="bg-white dark:bg-admin-slate-800 shadow-sm rounded-lg p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="w-full sm:w-56">
          <div className="h-4 w-24 bg-admin-slate-200 dark:bg-admin-slate-700 rounded mb-2"></div>
          <div className="h-10 w-full bg-admin-slate-200 dark:bg-admin-slate-700 rounded-lg"></div>
        </div>
        <div className="flex-1">
          <div className="h-4 w-32 bg-admin-slate-200 dark:bg-admin-slate-700 rounded mb-2"></div>
          <div className="h-10 w-full bg-admin-slate-200 dark:bg-admin-slate-700 rounded-lg"></div>
        </div>
      </div>
    </div>

    {/* Table Skeleton */}
    <div className="bg-white dark:bg-admin-slate-800 shadow-sm rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-admin-slate-200 dark:divide-admin-slate-700">
          <thead className="bg-admin-slate-50 dark:bg-admin-slate-800/50">
            <tr>
              {[...Array(7)].map((_, i) => (
                <th key={i} scope="col" className="px-6 py-3">
                  <div className="h-4 w-20 bg-admin-slate-200 dark:bg-admin-slate-700 rounded"></div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-admin-slate-800 divide-y divide-admin-slate-200 dark:divide-admin-slate-700">
            {[...Array(5)].map((_, i) => (
              <tr key={i}>
                <td className="px-6 py-4">
                  <div className="h-4 w-4 bg-admin-slate-200 dark:bg-admin-slate-700 rounded"></div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    <div className="h-10 w-10 bg-admin-slate-200 dark:bg-admin-slate-700 rounded-full"></div>
                    <div className="ml-4">
                      <div className="h-4 w-32 bg-admin-slate-200 dark:bg-admin-slate-700 rounded"></div>
                      <div className="mt-1 h-3 w-24 bg-admin-slate-200 dark:bg-admin-slate-700 rounded"></div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="h-4 w-12 bg-admin-slate-200 dark:bg-admin-slate-700 rounded"></div>
                </td>
                <td className="px-6 py-4">
                  <div className="h-4 w-20 bg-admin-slate-200 dark:bg-admin-slate-700 rounded"></div>
                </td>
                <td className="px-6 py-4">
                  <div className="h-6 w-16 bg-admin-slate-200 dark:bg-admin-slate-700 rounded-full"></div>
                </td>
                <td className="px-6 py-4">
                  <div className="h-4 w-24 bg-admin-slate-200 dark:bg-admin-slate-700 rounded"></div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end space-x-3">
                    <div className="h-4 w-16 bg-admin-slate-200 dark:bg-admin-slate-700 rounded"></div>
                    <div className="h-4 w-12 bg-admin-slate-200 dark:bg-admin-slate-700 rounded"></div>
                    <div className="h-4 w-14 bg-admin-slate-200 dark:bg-admin-slate-700 rounded"></div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const CustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const { isLoading, startLoading, stopLoading } = useLoading();
  const [error, setError] = useState(null);
  const { activeUserIds } = useSocket();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    fetchCustomers();
  }, []);

  // Deep-link support: /admin/customers?openCustomer=<id> opens that
  // customer's profile modal directly, e.g. from a "View Profile" link
  // on another admin page.
  useEffect(() => {
    const openCustomerId = searchParams.get('openCustomer');
    if (openCustomerId && customers.length > 0) {
      const customer = customers.find(c => c._id === openCustomerId);
      if (customer) {
        handleCustomerClick(customer);
      }
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('openCustomer');
      setSearchParams(nextParams, { replace: true });
    }
  }, [customers, searchParams]);

  const fetchCustomers = async () => {
    try {
      startLoading();
      const response = await api.get('/users');
      setCustomers(response.data.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch customers. Please try again later.');
      console.error('Error fetching customers:', err);
    } finally {
      stopLoading();
    }
  };

  const handleStatusChange = async (userId, newStatus, reason = '') => {
    try {
      startLoading();
      await api.put(`/users/${userId}/status`, {
        status: newStatus,
        blockReason: reason
      });
      fetchCustomers();
    } catch (err) {
      console.error('Error updating user status:', err);
    } finally {
      stopLoading();
    }
  };

  const handleBlock = (userId, email) => {
    const reason = window.prompt(`Please enter a reason for blocking ${email}:`);
    if (reason !== null) {
      handleStatusChange(userId, 'blocked', reason);
    }
  };


  const handleCustomerClick = (customer) => {
    setSelectedCustomer(customer);
    setIsProfileModalOpen(true);
  };

  const isCustomerOnline = (customerId) => {
    return activeUserIds.includes(customerId);
  };

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return (
      <div className="text-center text-red-600 p-4">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-admin-slate-900 dark:text-admin-slate-100">Customers</h1>
          <p className="mt-1 text-sm text-admin-slate-500 dark:text-admin-slate-400">
            Manage your customer base and view customer information
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-admin-ucla-500 hover:bg-admin-ucla-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-admin-ucla-500 dark:focus:ring-offset-admin-slate-800 transition-colors duration-200">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Add Customer
          </button>
        </div>
      </div>


      {/* Filters */}
      <div className="bg-white dark:bg-admin-slate-800 shadow-sm rounded-lg p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="w-full sm:w-56">
            <label htmlFor="status-select" className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-2">
              Filter by Status
            </label>
            <div className="relative">
              <select
                id="status-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full appearance-none rounded-lg border border-admin-slate-200 dark:border-admin-slate-700 bg-white dark:bg-admin-slate-800 py-2.5 pl-4 pr-10 text-sm font-medium text-admin-slate-900 dark:text-admin-slate-100 shadow-sm hover:border-admin-ucla-500 focus:border-admin-ucla-500 focus:outline-none focus:ring-1 focus:ring-admin-ucla-500"
              >
                <option value="all">All Customers</option>
                <option value="active">Active</option>
                <option value="blocked">Blocked</option>
                <option value="unverified">Unverified</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="h-5 w-5 text-admin-slate-400 dark:text-admin-slate-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>

          <div className="flex-1">
            <label htmlFor="search-customers" className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-2">
              Search Customers
            </label>
            <div className="relative">
              <input
                id="search-customers"
                type="text"
                placeholder="Search by name, email, or order ID..."
                className="w-full rounded-lg border border-admin-slate-200 dark:border-admin-slate-700 bg-white dark:bg-admin-slate-800 py-2.5 pl-10 pr-4 text-sm text-admin-slate-900 dark:text-admin-slate-100 placeholder-admin-slate-400 dark:placeholder-admin-slate-500 shadow-sm hover:border-admin-ucla-500 focus:border-admin-ucla-500 focus:outline-none focus:ring-1 focus:ring-admin-ucla-500"
              />
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <svg className="h-5 w-5 text-admin-slate-400 dark:text-admin-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Customers Table */}
      <div className="bg-white dark:bg-admin-slate-800 shadow-sm rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-admin-slate-200 dark:divide-admin-slate-700">
            <thead className="bg-admin-slate-50 dark:bg-admin-slate-800/50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-admin-slate-500 dark:text-admin-slate-400 uppercase tracking-wider">
                  Customer
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-admin-slate-500 dark:text-admin-slate-400 uppercase tracking-wider">
                  Phone
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-admin-slate-500 dark:text-admin-slate-400 uppercase tracking-wider">
                  Orders
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-admin-slate-500 dark:text-admin-slate-400 uppercase tracking-wider">
                  Total Spent
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-admin-slate-500 dark:text-admin-slate-400 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-admin-slate-500 dark:text-admin-slate-400 uppercase tracking-wider">
                  Last Purchase
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-admin-slate-500 dark:text-admin-slate-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-admin-slate-800 divide-y divide-admin-slate-200 dark:divide-admin-slate-700">
              {customers.map((customer) => (
                <CustomerRow
                  key={customer._id}
                  customer={customer}
                  onCustomerClick={handleCustomerClick}
                  isOnline={isCustomerOnline(customer._id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Customer Profile Modal */}
      <CustomerProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        customer={selectedCustomer}
      />
    </div>
  );
};

const CustomerProfileModal = ({ isOpen, onClose, customer }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [cartInfo, setCartInfo] = useState(null);
  const [loadingCart, setLoadingCart] = useState(false);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [loadingPurchases, setLoadingPurchases] = useState(false);

  useEffect(() => {
    if (isOpen && customer && activeTab === 'activity') {
      const fetchActivities = async () => {
        try {
          setLoadingActivities(true);
          const response = await api.get(`/activity/user/${customer._id}`);
          setActivities(response.data.data);
        } catch (error) {
          console.error('Failed to fetch user activities:', error);
        } finally {
          setLoadingActivities(false);
        }
      };
      fetchActivities();
    }
  }, [isOpen, customer, activeTab]);

  useEffect(() => {
    if (isOpen && customer && activeTab === 'cart') {
      const fetchCart = async () => {
        try {
          setLoadingCart(true);
          const response = await api.get(`/cart/admin/${customer._id}`);
          setCartInfo(response.data.data);
        } catch (error) {
          console.error('Failed to fetch cart info:', error);
        } finally {
          setLoadingCart(false);
        }
      };
      fetchCart();
    }
  }, [isOpen, customer, activeTab]);

  useEffect(() => {
    if (isOpen && customer && activeTab === 'purchase_history') {
      const fetchPurchases = async () => {
        try {
          setLoadingPurchases(true);
          const response = await api.get('/orders/admin/all', {
            params: { user: customer._id, limit: 100 }
          });
          setPurchaseHistory(response.data.data.orders);
        } catch (error) {
          console.error('Failed to fetch purchase history:', error);
        } finally {
          setLoadingPurchases(false);
        }
      };
      fetchPurchases();
    }
  }, [isOpen, customer, activeTab]);

  if (!isOpen || !customer) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="fixed inset-0 transition-opacity bg-admin-slate-500 bg-opacity-75" onClick={onClose}></div>
        
        <div className="relative bg-white dark:bg-admin-slate-800 rounded-lg shadow-xl w-full max-w-2xl mx-auto overflow-hidden">
          {/* Modal Header */}
          <div className="px-6 py-4 border-b border-admin-slate-200 dark:border-admin-slate-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-admin-slate-900 dark:text-admin-slate-100">
                Customer Profile
              </h3>
              <button
                onClick={onClose}
                className="text-admin-slate-400 hover:text-admin-slate-500 dark:text-admin-slate-500 dark:hover:text-admin-slate-400"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Modal Body */}
          <div className="px-6 py-6">
            {/* Tabs */}
            <div className="flex border-b border-admin-slate-200 dark:border-admin-slate-700 mb-6">
              <button
                className={`py-2 px-4 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'profile'
                    ? 'border-admin-ucla-500 text-admin-ucla-600 dark:text-admin-ucla-400'
                    : 'border-transparent text-admin-slate-500 hover:text-admin-slate-700 dark:text-admin-slate-400 dark:hover:text-admin-slate-300'
                }`}
                onClick={() => setActiveTab('profile')}
              >
                Profile Details
              </button>
              <button
                className={`py-2 px-4 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'activity'
                    ? 'border-admin-ucla-500 text-admin-ucla-600 dark:text-admin-ucla-400'
                    : 'border-transparent text-admin-slate-500 hover:text-admin-slate-700 dark:text-admin-slate-400 dark:hover:text-admin-slate-300'
                }`}
                onClick={() => setActiveTab('activity')}
              >
                Activity Log
              </button>
              <button
                className={`py-2 px-4 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                  activeTab === 'cart'
                    ? 'border-admin-ucla-500 text-admin-ucla-600 dark:text-admin-ucla-400'
                    : 'border-transparent text-admin-slate-500 hover:text-admin-slate-700 dark:text-admin-slate-400 dark:hover:text-admin-slate-300'
                }`}
                onClick={() => setActiveTab('cart')}
              >
                Cart Info
              </button>
              <button
                className={`py-2 px-4 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                  activeTab === 'purchase_history'
                    ? 'border-admin-ucla-500 text-admin-ucla-600 dark:text-admin-ucla-400'
                    : 'border-transparent text-admin-slate-500 hover:text-admin-slate-700 dark:text-admin-slate-400 dark:hover:text-admin-slate-300'
                }`}
                onClick={() => setActiveTab('purchase_history')}
              >
                Purchase History
              </button>
            </div>

            {activeTab === 'profile' && (
              <>
                {/* Customer Avatar and Name */}
                <div className="flex items-center mb-6">
                  <div className="h-16 w-16 rounded-full bg-admin-ucla-100 dark:bg-admin-ucla-900/30 flex items-center justify-center">
                    <span className="text-admin-ucla-600 dark:text-admin-ucla-400 font-medium text-2xl">
                      {customer.name.charAt(0)}
                    </span>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-xl font-semibold text-admin-slate-900 dark:text-admin-slate-100">
                      {customer.name}
                    </h4>
                    <p className="text-sm text-admin-slate-500 dark:text-admin-slate-400">
                      {customer.email}
                    </p>
                  </div>
                </div>

                {/* Customer Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-admin-slate-50 dark:bg-admin-slate-700/50 p-4 rounded-lg">
                    <p className="text-xs font-medium text-admin-slate-500 dark:text-admin-slate-400 uppercase tracking-wider mb-1">
                      Phone
                    </p>
                    <p className="text-sm font-medium text-admin-slate-900 dark:text-admin-slate-100">
                      {customer.phone || 'Not provided'}
                    </p>
                  </div>

                  <div className="bg-admin-slate-50 dark:bg-admin-slate-700/50 p-4 rounded-lg">
                    <p className="text-xs font-medium text-admin-slate-500 dark:text-admin-slate-400 uppercase tracking-wider mb-1">
                      Status
                    </p>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      customer.status === 'active' 
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' 
                        : customer.status === 'blocked' 
                          ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400' 
                          : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400'
                    }`}>
                      {customer.status}
                    </span>
                  </div>

                  <div className="bg-admin-slate-50 dark:bg-admin-slate-700/50 p-4 rounded-lg">
                    <p className="text-xs font-medium text-admin-slate-500 dark:text-admin-slate-400 uppercase tracking-wider mb-1">
                      Total Orders
                    </p>
                    <p className="text-sm font-medium text-admin-slate-900 dark:text-admin-slate-100">
                      {customer.totalOrders || 0}
                    </p>
                  </div>

                  <div className="bg-admin-slate-50 dark:bg-admin-slate-700/50 p-4 rounded-lg">
                    <p className="text-xs font-medium text-admin-slate-500 dark:text-admin-slate-400 uppercase tracking-wider mb-1">
                      Total Spent
                    </p>
                    <p className="text-sm font-medium text-admin-slate-900 dark:text-admin-slate-100">
                      Rs. {customer.totalSpent || 0}
                    </p>
                  </div>

                  <div className="bg-admin-slate-50 dark:bg-admin-slate-700/50 p-4 rounded-lg">
                    <p className="text-xs font-medium text-admin-slate-500 dark:text-admin-slate-400 uppercase tracking-wider mb-1">
                      Last Purchase
                    </p>
                    <p className="text-sm font-medium text-admin-slate-900 dark:text-admin-slate-100">
                      {formatDate(customer.lastPurchase) || 'No purchases yet'}
                    </p>
                    {customer.lastPurchaseValue && (
                      <p className="text-xs text-admin-slate-600 dark:text-admin-slate-400 mt-1">
                        Rs. {customer.lastPurchaseValue.toLocaleString()}
                      </p>
                    )}
                  </div>

                  <div className="bg-admin-slate-50 dark:bg-admin-slate-700/50 p-4 rounded-lg">
                    <p className="text-xs font-medium text-admin-slate-500 dark:text-admin-slate-400 uppercase tracking-wider mb-1">
                      Member Since
                    </p>
                    <p className="text-sm font-medium text-admin-slate-900 dark:text-admin-slate-100">
                      {formatDate(customer.createdAt)}
                    </p>
                  </div>
                </div>

                {/* Block Reason if blocked */}
                {customer.status === 'blocked' && customer.blockReason && (
                  <div className="mt-4 bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
                    <p className="text-xs font-medium text-red-600 dark:text-red-400 uppercase tracking-wider mb-1">
                      Block Reason
                    </p>
                    <p className="text-sm text-red-800 dark:text-red-300">
                      {customer.blockReason}
                    </p>
                  </div>
                )}
              </>
            )}

            {activeTab === 'activity' && (
              <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
                {loadingActivities ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-admin-ucla-500 mx-auto"></div>
                    <p className="mt-4 text-admin-slate-500">Loading activity log...</p>
                  </div>
                ) : activities.length > 0 ? (
                  <div className="relative border-l-2 border-admin-slate-200 dark:border-admin-slate-700 ml-4 mt-2">
                    {activities.map((activity, index) => {
                      let Icon = MousePointerClick;
                      let iconColor = 'bg-admin-slate-100 text-admin-slate-500 dark:bg-admin-slate-800 dark:text-admin-slate-400';
                      let dotColor = 'bg-admin-slate-400';
                      let borderColor = 'border-admin-slate-200 dark:border-admin-slate-700';
                      
                      // Determine UI based on action
                      if (activity.action === 'ADD_TO_CART') {
                        Icon = ShoppingCart;
                        iconColor = 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400';
                        dotColor = 'bg-blue-500';
                        borderColor = 'border-blue-100 dark:border-blue-800';
                      } else if (activity.action === 'VIEW_PRODUCT') {
                        Icon = Eye;
                        iconColor = 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400';
                        dotColor = 'bg-purple-500';
                        borderColor = 'border-purple-100 dark:border-purple-800';
                      } else if (activity.action === 'SEARCH') {
                        Icon = Search;
                        iconColor = 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400';
                        dotColor = 'bg-amber-500';
                        borderColor = 'border-amber-100 dark:border-amber-800';
                      } else if (activity.action === 'PAGE_VIEW') {
                        Icon = MousePointerClick;
                        iconColor = 'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400';
                        dotColor = 'bg-teal-500';
                        borderColor = 'border-teal-100 dark:border-teal-800';
                      }

                      return (
                        <div key={activity._id || index} className="mb-8 ml-8 relative group">
                          {/* Timeline Dot */}
                          <div className={`absolute w-4 h-4 rounded-full -left-[41px] top-1 border-2 border-white dark:border-admin-slate-800 ${dotColor}`}></div>
                          
                          {/* Activity Card */}
                          <div className={`bg-white dark:bg-admin-slate-800/80 rounded-xl border ${borderColor} p-4 shadow-sm hover:shadow-md transition-shadow duration-200`}>
                            
                            {/* Header: Action & Time */}
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-lg ${iconColor}`}>
                                  <Icon size={18} />
                                </div>
                                <div>
                                  <h5 className="font-semibold text-admin-slate-800 dark:text-admin-slate-100 capitalize">
                                    {activity.action.replace(/_/g, ' ').toLowerCase()}
                                  </h5>
                                  <p className="text-xs text-admin-slate-500 dark:text-admin-slate-400 flex items-center gap-1 mt-0.5">
                                    <Clock size={12} />
                                    {new Date(activity.timestamp).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
                                  </p>
                                </div>
                              </div>
                              
                              {/* Duration Badge if timeSpent exists */}
                              {activity.metadata && activity.metadata.timeSpent && (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-admin-slate-100 text-admin-slate-600 dark:bg-admin-slate-700 dark:text-admin-slate-300">
                                  <Clock size={12} />
                                  {activity.metadata.timeSpent}s spent
                                </span>
                              )}
                            </div>

                            {/* Content based on Action */}
                            
                            {/* Product Card Rendering */}
                            {(activity.action === 'VIEW_PRODUCT' || activity.action === 'ADD_TO_CART') && activity.targetId && (
                              <div className="mt-3 flex gap-4 p-3 bg-admin-slate-50 dark:bg-admin-slate-900/50 rounded-lg border border-admin-slate-100 dark:border-admin-slate-700">
                                {/* Product Image */}
                                <div className="w-16 h-16 rounded-md bg-white dark:bg-admin-slate-800 border border-admin-slate-200 dark:border-admin-slate-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                                  {activity.targetId.images && activity.targetId.images.length > 0 ? (
                                    <img 
                                      src={activity.targetId.images[0].url} 
                                      alt={activity.targetId.name} 
                                      className="w-full h-full object-contain"
                                    />
                                  ) : (
                                    <ShoppingBag size={20} className="text-admin-slate-300" />
                                  )}
                                </div>
                                
                                {/* Product Info */}
                                <div className="flex-1 flex flex-col justify-center">
                                  <p className="font-medium text-admin-slate-800 dark:text-admin-slate-200 text-sm line-clamp-1">
                                    {activity.targetId.name}
                                  </p>
                                  
                                  {/* Price and Add-on info */}
                                  <div className="flex items-center gap-4 mt-1">
                                    {(activity.targetId.regularPrice || activity.targetId.salePrice) && (
                                      <p className="text-sm font-semibold text-admin-ucla-600 dark:text-admin-ucla-400">
                                        Rs. {activity.targetId.salePrice || activity.targetId.regularPrice}
                                      </p>
                                    )}
                                    
                                    {activity.action === 'ADD_TO_CART' && activity.metadata?.quantity && (
                                      <span className="text-xs font-medium text-admin-slate-500 bg-admin-slate-200 dark:bg-admin-slate-700 dark:text-admin-slate-300 px-2 py-0.5 rounded">
                                        Qty: {activity.metadata.quantity}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Action Link */}
                                {activity.targetId.slug && (
                                  <a 
                                    href={`/product/${activity.targetId.slug}`} 
                                    target="_blank" 
                                    rel="noreferrer"
                                    className="self-center p-2 text-admin-slate-400 hover:text-admin-ucla-500 hover:bg-admin-ucla-50 dark:hover:bg-admin-ucla-900/30 rounded-full transition-colors"
                                  >
                                    <ArrowRight size={16} />
                                  </a>
                                )}
                              </div>
                            )}

                            {/* Search Query Rendering */}
                            {activity.action === 'SEARCH' && activity.metadata && activity.metadata.query && (
                              <div className="mt-2 p-3 bg-admin-slate-50 dark:bg-admin-slate-900/50 rounded-lg border border-admin-slate-100 dark:border-admin-slate-700 flex items-center gap-2">
                                <Search size={16} className="text-admin-slate-400" />
                                <span className="text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300">
                                  Searched for <span className="text-admin-ucla-600 dark:text-admin-ucla-400">"{activity.metadata.query}"</span>
                                </span>
                                {activity.metadata.resultsCount !== undefined && (
                                  <span className="text-xs text-admin-slate-500 ml-auto">
                                    {activity.metadata.resultsCount} results
                                  </span>
                                )}
                              </div>
                            )}
                            
                            {/* Fallback metadata if not a known action type or missing target info */}
                            {activity.action !== 'SEARCH' && activity.action !== 'ADD_TO_CART' && activity.action !== 'VIEW_PRODUCT' && activity.metadata && Object.keys(activity.metadata).length > 0 && (
                              <div className="mt-3 text-xs text-admin-slate-500 bg-admin-slate-50 dark:bg-admin-slate-900/50 p-2 rounded border border-admin-slate-100 dark:border-admin-slate-800 overflow-x-auto">
                                <pre className="font-mono">{JSON.stringify(activity.metadata, null, 2)}</pre>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-admin-slate-400">
                    <MousePointerClick size={48} className="mb-4 text-admin-slate-200 dark:text-admin-slate-700" />
                    <p className="text-lg font-medium text-admin-slate-500">No activity recorded yet</p>
                    <p className="text-sm mt-1">Customer hasn't interacted with the store recently.</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'cart' && (
              <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
                {loadingCart ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-admin-ucla-500 mx-auto"></div>
                    <p className="mt-4 text-admin-slate-500">Loading cart info...</p>
                  </div>
                ) : cartInfo && cartInfo.items && cartInfo.items.length > 0 ? (
                  <div>
                    <div className="flex justify-between items-center bg-admin-slate-50 dark:bg-admin-slate-700/50 p-4 rounded-lg mb-4">
                      <span className="font-medium text-admin-slate-700 dark:text-admin-slate-300">Total Cart Value:</span>
                      <span className="font-bold text-admin-slate-900 dark:text-admin-slate-100">
                        Rs. {cartInfo.items.reduce((total, item) => total + ((item.product?.specialOfferPrice || item.product?.regularPrice || 0) * item.quantity), 0)}
                      </span>
                    </div>
                    <div className="space-y-3">
                      {cartInfo.items.map((item, idx) => (
                        <div key={idx} className="flex gap-4 p-3 border border-admin-slate-200 dark:border-admin-slate-700 rounded-lg bg-white dark:bg-admin-slate-800">
                          <div className="w-16 h-16 rounded bg-admin-slate-100 dark:bg-admin-slate-900 flex-shrink-0 overflow-hidden flex items-center justify-center">
                            {item.product && item.product.images && item.product.images.length > 0 ? (
                              <img src={item.product.images[0]} alt="Product" className="w-full h-full object-contain" />
                            ) : (
                              <ShoppingBag size={20} className="text-admin-slate-400" />
                            )}
                          </div>
                          <div className="flex-1">
                            <h5 className="font-medium text-admin-slate-900 dark:text-admin-slate-100 text-sm">
                              {item.product ? item.product.name : 'Unknown Product'}
                            </h5>
                            <div className="flex justify-between items-center mt-2">
                              <span className="text-xs text-admin-slate-500 bg-admin-slate-100 dark:bg-admin-slate-700 px-2 py-1 rounded">
                                Qty: {item.quantity}
                              </span>
                              <span className="text-sm font-semibold text-admin-slate-900 dark:text-admin-slate-100">
                                Rs. {(item.product?.specialOfferPrice || item.product?.regularPrice || 0) * item.quantity}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-admin-slate-400">
                    <ShoppingCart size={48} className="mb-4 text-admin-slate-200 dark:text-admin-slate-700" />
                    <p className="text-lg font-medium text-admin-slate-500">Cart is empty</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'purchase_history' && (
              <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
                {loadingPurchases ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-admin-ucla-500 mx-auto"></div>
                    <p className="mt-4 text-admin-slate-500">Loading purchase history...</p>
                  </div>
                ) : purchaseHistory && purchaseHistory.length > 0 ? (
                  <div className="space-y-4">
                    {purchaseHistory.map((order) => (
                      <div key={order._id} className="border border-admin-slate-200 dark:border-admin-slate-700 rounded-lg bg-white dark:bg-admin-slate-800 overflow-hidden">
                        <div className="bg-admin-slate-50 dark:bg-admin-slate-900/50 p-4 border-b border-admin-slate-200 dark:border-admin-slate-700 flex flex-wrap justify-between gap-4">
                          <div>
                            <p className="text-xs text-admin-slate-500 uppercase tracking-wider mb-1">Order Placed</p>
                            <p className="text-sm font-medium text-admin-slate-900 dark:text-admin-slate-100 flex items-center gap-1">
                              <Calendar size={14} />
                              {new Date(order.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-admin-slate-500 uppercase tracking-wider mb-1">Total</p>
                            <p className="text-sm font-medium text-admin-slate-900 dark:text-admin-slate-100">Rs. {order.total}</p>
                          </div>
                          <div>
                            <p className="text-xs text-admin-slate-500 uppercase tracking-wider mb-1">Order ID</p>
                            <p className="text-sm font-medium text-admin-slate-900 dark:text-admin-slate-100 uppercase">#{order._id.substring(0, 8)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-admin-slate-500 uppercase tracking-wider mb-1">Status</p>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              order.status === 'delivered' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                              order.status === 'processing' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                              'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                            }`}>
                              {order.status}
                            </span>
                          </div>
                        </div>
                        <div className="p-4 space-y-3">
                          {order.items.map((item, idx) => (
                            <div key={idx} className="flex gap-4">
                              <div className="w-12 h-12 rounded bg-admin-slate-100 dark:bg-admin-slate-900 flex-shrink-0 flex items-center justify-center overflow-hidden">
                                {item.product && item.product.images && item.product.images.length > 0 ? (
                                  <img src={item.product.images[0]} alt="Product" className="w-full h-full object-contain" />
                                ) : (
                                  <Package size={16} className="text-admin-slate-400" />
                                )}
                              </div>
                              <div>
                                <h6 className="text-sm font-medium text-admin-slate-900 dark:text-admin-slate-100">
                                  {item.product ? item.product.name : 'Unknown Product'}
                                </h6>
                                <p className="text-xs text-admin-slate-500 mt-1">
                                  Qty: {item.quantity} | Price: Rs. {item.price}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-admin-slate-400">
                    <Package size={48} className="mb-4 text-admin-slate-200 dark:text-admin-slate-700" />
                    <p className="text-lg font-medium text-admin-slate-500">No purchase history</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="px-6 py-4 bg-admin-slate-50 dark:bg-admin-slate-700/50 border-t border-admin-slate-200 dark:border-admin-slate-700 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-admin-ucla-500 text-white text-sm font-medium rounded-md hover:bg-admin-ucla-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-admin-ucla-500 dark:focus:ring-offset-admin-slate-800 transition-colors duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomersPage;